import { Query, QueryClient } from '@tanstack/react-query';

// One wait per query: every change during the first fetch is covered by the same refetch.
const waiting = new WeakSet<Query>();

/**
 * Invalidates one query. An invalidation during a first fetch joins that fetch instead of
 * replacing it, and the response may predate the change, so it runs again when the fetch lands.
 */
export const invalidateQuery = (queryClient: QueryClient, query: Query) => {
  const invalidate = () => queryClient.invalidateQueries({ queryKey: query.queryKey, exact: true });

  if (query.state.data !== undefined || query.state.fetchStatus !== 'fetching') {
    void invalidate();
    return;
  }

  if (waiting.has(query)) {
    return;
  }
  waiting.add(query);
  const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
    if (event.query !== query) {
      return;
    }
    if (event.type === 'removed') {
      waiting.delete(query);
      unsubscribe();
      return;
    }
    if (event.type === 'updated' && query.state.fetchStatus === 'idle') {
      waiting.delete(query);
      unsubscribe();
      void invalidate();
    }
  });
};
